/**
 * The upper bar and tool families use their approved Lucide preview icons, with Nova Motion for Animate.
 * Nova Line for other editor controls; More uses Nova Solid in every state.
 * Position controls use the matched Flobi geometry in design/editor-ui/position-icons.md.
 * Stroke controls use Streamline Core Line; provenance in design/editor-ui/stroke-icons.json.
 * Specialist icons retain their existing assets. All icons are bundled locally.
 * Provenance: design/editor-ui/layer-sidebar/nova-icons.json.
 */
export type IconName =
  // the Quiet groups upper bar (Lucide; provenance in assets/icons/toolbar-icons.md)
  | 'toolbar-files'
  | 'toolbar-chevron'
  | 'toolbar-undo'
  | 'toolbar-redo'
  | 'toolbar-layers'
  | 'toolbar-properties'
  | 'toolbar-notifications'
  | 'toolbar-share'
  | 'toolbar-export'
  | 'toolbar-comments'
  // The dock's marks (Classic dock, 2 Nov): Streamline Core Line; provenance in assets/icons/toolbar-icons.md
  | 'dock-select'
  | 'dock-rect'
  | 'dock-ellipse'
  | 'dock-polygon'
  | 'dock-star'
  | 'dock-text'
  | 'dock-draw'
  | 'dock-pen'
  | 'dock-brush'
  | 'dock-combine'
  | 'dock-cut'
  | 'dock-repeat'
  | 'dock-artboard'
  | 'dock-measure'
  | 'dock-comment'
  | 'dock-edit'
  | 'dock-animate'
  | 'sel-ai'
  | 'sel-align-left' | 'sel-align-center' | 'sel-align-right' | 'sel-align-justify'
  | 'sel-arrange'
  | 'sel-bold'
  | 'sel-border'
  | 'sel-chevron'
  | 'sel-colour'
  | 'sel-crop'
  | 'sel-dropper'
  | 'sel-effects'
  | 'sel-italic'
  | 'sel-layerize'
  | 'sel-minus'
  | 'sel-more'
  | 'sel-opacity'
  | 'sel-plus'
  | 'sel-remove-bg'
  | 'sel-replace'
  | 'sel-underline'
  | 'sel-vectorize'
  | 'menu-align-bottom'
  | 'menu-align-center'
  | 'menu-align-left'
  | 'menu-align-middle'
  | 'menu-align-right'
  | 'menu-align-top'
  | 'menu-bring-front' | 'menu-send-back' | 'menu-bring-forward'
  | 'menu-circle'
  | 'menu-copy'
  | 'menu-exclude'
  | 'menu-expand'
  | 'menu-export'
  | 'menu-eye'
  | 'menu-eye-off'
  | 'menu-flip-h'
  | 'menu-flip-v'
  | 'menu-info'
  | 'menu-intersect'
  | 'menu-lock'
  | 'menu-mask'
  | 'menu-properties'
  | 'menu-scissors'
  | 'menu-send-backward'
  | 'menu-square'
  | 'menu-star'
  | 'menu-subtract'
  | 'menu-text'
  | 'menu-text-area'
  | 'menu-trash'
  | 'menu-union'
  | 'menu-zoom-in'
  | 'menu-zoom-out'
  // Grouped outline tool families (Lucide; 18px display, 2px source strokes)
  | 'tools-select'
  | 'tools-shapes'
  | 'tools-text'
  | 'tools-draw'
  | 'tools-modify'
  | 'tools-repeat'
  | 'tools-layout'
  // Split navigator (Lucide; 16px marks with 1.7px source strokes)
  | 'nav-collapse'
  | 'nav-layers'
  | 'nav-folder'
  | 'nav-artboard'
  | 'nav-text'
  | 'nav-shape'
  | 'nav-path'
  | 'nav-image'
  | 'nav-eye'
  | 'nav-eye-off'
  | 'nav-lock'
  | 'nav-unlock'
  | 'nav-chevron'
  | 'nav-chevron-right'
  // chrome
  | 'files'
  | 'back'
  | 'arrow-up'
  | 'chevron'
  | 'chevron-right'
  | 'chevron-up-down'
  | 'sidebar-left'
  | 'sidebar-right'
  | 'more'
  | 'mode-edit'
  | 'present'
  | 'grip-vertical'
  | 'close'
  | 'check'
  | 'export'
  | 'export-download'
  | 'format-image'
  | 'format-vector'
  | 'format-pdf'
  | 'format-video'
  | 'format-animation'
  | 'undo'
  | 'redo'
  | 'hand'
  | 'zoom-in'
  | 'zoom-out'
  | 'fit'
  | 'actual-size'
  | 'full-screen'
  | 'pencil'
  | 'play'
  | 'stop'
  | 'search'
  | 'filter'
  | 'plus'
  | 'minus'
  | 'cloud-check'
  | 'cloud-off'
  | 'warning'
  | 'info'
  | 'settings'
  | 'stroke-settings'
  | 'stroke-weight'
  | 'stroke-cap-flat'
  | 'stroke-cap-round'
  | 'stroke-cap-square'
  | 'stroke-join-miter'
  | 'stroke-join-round'
  | 'stroke-join-bevel'
  | 'document'
  | 'artboard'
  | 'add-artboard'
  | 'portrait'
  | 'landscape'
  | 'sparkles'
  | 'motion'
  | 'timer'
  | 'sun'
  | 'moon'
  | 'display'
  | 'keyboard'
  // tools
  | 'cursor'
  | 'pen'
  | 'draw'
  | 'brush'
  | 'magic'
  | 'generate-sparkles'
  | 'shape'
  | 'square'
  | 'circle'
  | 'polygon'
  | 'star'
  | 'line'
  | 'arc'
  | 'text'
  | 'text-area'
  | 'text-path'
  | 'free-transform'
  | 'transform-scale'
  | 'transform-perspective'
  | 'transform-distort'
  | 'pin-warp'
  | 'perspective'
  | 'distort'
  | 'pin'
  | 'width'
  | 'warp'
  | 'twirl'
  | 'pucker'
  | 'bloat'
  | 'scallop'
  | 'wrinkle'
  | 'blend'
  | 'smooth'
  | 'blend-steps'
  | 'cloner'
  | 'cloner-linear'
  | 'cloner-radial'
  | 'combine'
  | 'eraser'
  | 'scissors'
  | 'knife'
  | 'dimension'
  | 'dimension-angle'
  | 'dimension-radius'
  | 'slice'
  | 'ruler'
  | 'layers'
  // the four ways of pointing
  | 'select-object'
  | 'select-point'
  | 'select-area'
  | 'select-lasso'
  | 'invert'
  | 'mirror'
  // shape ops and arranging
  | 'union'
  | 'subtract'
  | 'intersect'
  | 'exclude'
  | 'group'
  | 'ungroup'
  | 'bring-forward'
  | 'send-backward'
  | 'align-left'
  | 'align-center'
  | 'align-right'
  | 'align-top'
  | 'align-middle'
  | 'align-bottom'
  | 'distribute-h'
  | 'distribute-v'
  | 'flip-h'
  | 'flip-v'
  | 'rotate'
  | 'angle'
  | 'move-x'
  | 'move-y'
  | 'spacing'
  | 'expand'
  | 'release'
  | 'join'
  // paint and appearance
  | 'weight'
  | 'opacity'
  | 'corner'
  | 'eyedropper'
  | 'palette'
  | 'gradient-linear'
  | 'gradient-radial'
  | 'gradient-conic'
  | 'shade'
  | 'mask'
  | 'transparency'
  | 'eye'
  | 'eye-off'
  | 'lock'
  | 'unlock'
  | 'copy'
  | 'trash'
  // type
  | 'font'
  | 'selection-bold'
  | 'selection-italic'
  | 'selection-underline'
  | 'selection-strikethrough'
  | 'selection-case-sensitive'
  | 'selection-text-align-justify'
  | 'selection-radius'
  | 'selection-crop'
  | 'selection-frame-fill'
  | 'selection-ratio'
  | 'selection-opacity'
  | 'text-align-left'
  | 'text-align-center'
  | 'text-align-right'
  // view
  | 'grid'
  | 'guides'
  | 'smart-guides'
  | 'magnet'
  | 'snap-grid'
  // the cloner's effectors and fields
  | 'effector-plain'
  | 'effector-random'
  | 'effector-step'
  | 'field-none'
  | 'field-linear'
  | 'field-radial'
  | 'field-box'
  // the library
  | 'upload'
  | 'folder'
  | 'brands'
  | 'templates'
  | 'addons'
  | 'elements'
  | 'reference-audio'
  | 'ref-character'
  | 'ref-style'
  // Elements: Flow's type and trait glyphs (via Director's copy)
  | 'el-ico-categories'
  | 'el-ico-dashboard'
  | 'el-ico-device-laptop'
  | 'el-ico-expression'
  | 'el-ico-gallery'
  | 'el-ico-image'
  | 'el-ico-ipad-tablet'
  | 'el-ico-left-half-shadow'
  | 'el-ico-location-target'
  | 'el-ico-notes'
  | 'el-ico-pose'
  | 'el-ico-rect-split'
  | 'el-ico-ruler-angle'
  | 'el-ico-select-square-area'
  | 'el-ico-size'
  | 'el-ico-snow-flake'
  | 'el-ico-sticky'
  | 'el-ico-sunset'
  | 'el-ico-voice'
  | 'el-ico-wardrobe'
  | 'el-tab-character'
  | 'el-tab-environment'
  | 'el-tab-general'
  | 'el-tab-product'
  | 'el-tab-screen'
  | 'el-tab-style'
  | 'el-tab-typeface'
  | 'pictures'
  | 'adjust'
  | 'actions'
  // the file browser (Nova Line)
  | 'clock'
  | 'person'
  | 'people'
  | 'workspace'
  | 'list'
  | 'lightbulb'
  // the properties panel's own marks: one stroke, one grid (2 Nov)
  | 'prop-text-left'
  | 'prop-text-center'
  | 'prop-text-right'
  | 'prop-text-justify'
  | 'prop-rotate'
  | 'prop-opacity'
  | 'prop-line-height'
  | 'prop-letter-spacing'
  | 'prop-font-size'
  | 'prop-align-top'
  | 'prop-align-middle'
  | 'prop-align-bottom';

const LINE = import.meta.glob('../../assets/icons/line/*.svg', { eager: true, query: '?url', import: 'default' }) as Record<string, string>;
const SOLID = import.meta.glob('../../assets/icons/solid/*.svg', { eager: true, query: '?url', import: 'default' }) as Record<string, string>;

const byName = (files: Record<string, string>) =>
  new Map(Object.entries(files).map(([path, url]) => [path.slice(path.lastIndexOf('/') + 1, -4), url]));

const LINE_ART = byName(LINE);
const SOLID_ART = byName(SOLID);

/** Marks already reported missing, so a render loop does not bury the console. */
const MISSING = new Set<string>();

/** Glyph size is independent of the surrounding mouse or touch target. */
export const ICON_SIZE = { indicator: 10, small: 14, control: 16, navigation: 18 } as const;

export function Icon({ name, size = ICON_SIZE.control, solid = false }: { name: IconName; size?: number; solid?: boolean }) {
  const art = (solid ? SOLID_ART.get(name) : undefined) ?? LINE_ART.get(name);
  if (!art) {
    if (import.meta.env.DEV && !MISSING.has(name)) {
      MISSING.add(name);
      console.warn(`[icons] no mark called "${name}"`);
    }
    return <span aria-hidden data-icon={name} className="inline-block shrink-0 align-middle" style={{ width: size, height: size }} />;
  }
  // Quoted, and asked for as a url rather than inlined: Vite turns a small svg into a
  // data uri whose own markup carries quotes, which makes an unquoted url() invalid —
  // and an invalid mask is not an error, it is a solid square where the mark should be.
  const mask = `url("${art}")`;
  return (
    <span
      aria-hidden
      data-icon={name}
      className="inline-block shrink-0 align-middle bg-current"
      style={{
        width: size,
        height: size,
        // The cursor's visual weight sits above-left of its bounding-box center.
        // Center the silhouette in controls without changing the canvas hotspot.
        transform: name === 'cursor' ? 'translate(6.5%, 6.5%)' : undefined,
        maskImage: mask,
        WebkitMaskImage: mask,
        maskSize: 'contain',
        WebkitMaskSize: 'contain',
        maskRepeat: 'no-repeat',
        WebkitMaskRepeat: 'no-repeat',
        maskPosition: 'center',
        WebkitMaskPosition: 'center',
      }}
    />
  );
}
